import { useState } from "react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Check, Clock, Undo2, Loader2 } from "lucide-react";
import type { DerivedStage, StageKey } from "@/lib/lrDirect/deriveStages";

interface Props {
  stage: DerivedStage;
  allStages: DerivedStage[];
  isBackfilled?: boolean;
  /** When the load is Closed, operational undo is disabled until Reopen Load. */
  closed?: boolean;
  onRequestReopen?: () => void;
  children: React.ReactNode;
  onMarkDone: (stage: DerivedStage) => Promise<void> | void;
  onUndo: (stage: DerivedStage, alsoCleared: StageKey[]) => Promise<void> | void;
  onEditTime: (stage: DerivedStage, newAtIso: string) => Promise<void> | void;
}


function toLocalDatetimeInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalDatetimeInput(v: string): string { return new Date(v).toISOString(); }

function isBrokerStage(s: DerivedStage): boolean {
  return s.kind === "broker_stop" || s.kind === "broker_dispatch";
}

export function StagePopover({
  stage, allStages, isBackfilled, closed, onRequestReopen, children,
  onMarkDone, onUndo, onEditTime,
}: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [timeVal, setTimeVal] = useState(() => toLocalDatetimeInput(stage.at ?? new Date().toISOString()));
  const [confirmUndo, setConfirmUndo] = useState(false);

  const stageIdx = allStages.findIndex((s) => s.key === stage.key);

  // Later broker stages currently done — cleared on undo (cascade).
  const laterCleared: StageKey[] = allStages
    .slice(stageIdx + 1)
    .filter((s) => isBrokerStage(s) && s.status === "done")
    .map((s) => s.key);

  // Prior broker stages not yet done — backfilled when marking this stage done directly.
  const backfillStages: StageKey[] = stage.status === "done"
    ? []
    : allStages
        .slice(0, stageIdx)
        .filter((s) => isBrokerStage(s) && s.status !== "done")
        .map((s) => s.key);
  const willBackfill = backfillStages.length > 0;

  const handleMarkDone = async () => {
    setBusy(true);
    try { await onMarkDone(stage); setOpen(false); }
    catch (e: any) { toast.error(e?.message || "Failed to update"); }
    finally { setBusy(false); }
  };
  const handleUndo = async () => {
    setBusy(true);
    try { await onUndo(stage, laterCleared); setConfirmUndo(false); setOpen(false); }
    catch (e: any) { toast.error(e?.message || "Undo failed"); }
    finally { setBusy(false); }
  };
  const handleSaveTime = async () => {
    if (!timeVal) return;
    setBusy(true);
    try { await onEditTime(stage, fromLocalDatetimeInput(timeVal)); setEditing(false); setOpen(false); }
    catch (e: any) { toast.error(e?.message || "Could not update time"); }
    finally { setBusy(false); }
  };

  const stageLabel = (k: StageKey) => allStages.find((s) => s.key === k)?.label ?? k;

  return (
    <>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(false); }}>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent side="top" align="center" className="w-64 p-3">
          <div className="mb-2">
            <div className="text-sm font-semibold">{stage.label}</div>
            {stage.status === "done" && stage.at && (
              <div className="text-[11px] text-muted-foreground">
                Set {new Date(stage.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                {isBackfilled ? " · auto-backfilled" : ""}
              </div>
            )}
            {stage.status !== "done" && (
              <div className="text-[11px] text-muted-foreground">
                {willBackfill
                  ? `Marks done + backfills: ${backfillStages.map(stageLabel).join(", ")}`
                  : "Marks this stage done now"}
              </div>
            )}
          </div>

          {editing ? (
            <div className="space-y-2">
              <label className="text-[11px] font-medium text-muted-foreground">New time</label>
              <Input type="datetime-local" value={timeVal} onChange={(e) => setTimeVal(e.target.value)} className="h-8 text-xs" />
              <div className="flex gap-1.5">
                <Button size="sm" className="h-7 text-xs flex-1" onClick={handleSaveTime} disabled={busy}>
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing(false)} disabled={busy}>Cancel</Button>
              </div>
            </div>
          ) : closed ? (
            <div className="space-y-2">
              <p className="text-[11px] text-muted-foreground">
                This load is closed. Reopen it before correcting milestones.
              </p>
              {onRequestReopen && (
                <Button
                  size="sm" variant="outline" className="w-full h-8 text-xs gap-1.5"
                  onClick={() => { setOpen(false); onRequestReopen(); }}
                >
                  <Undo2 className="h-3.5 w-3.5" /> Reopen Load
                </Button>
              )}
            </div>
          ) : stage.status === "done" ? (
            <div className="flex flex-col gap-1.5">
              <Button size="sm" variant="outline" className="h-8 text-xs justify-start gap-2" onClick={() => setEditing(true)}>
                <Clock className="h-3.5 w-3.5" /> Change time
              </Button>
              <Button
                size="sm" variant="outline"
                className="h-8 text-xs justify-start gap-2 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                onClick={() => setConfirmUndo(true)}
              >
                <Undo2 className="h-3.5 w-3.5" /> Undo this stage
              </Button>
            </div>
          ) : (
            <Button size="sm" className="w-full h-8 text-xs gap-1.5" onClick={handleMarkDone} disabled={busy}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Mark as done
            </Button>
          )}
        </PopoverContent>
      </Popover>

      <AlertDialog open={confirmUndo} onOpenChange={setConfirmUndo}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Undo &quot;{stage.label}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              {laterCleared.length > 0 ? (
                <>
                  This will also clear the following later stage{laterCleared.length > 1 ? "s" : ""}:{" "}
                  <span className="font-medium text-foreground">{laterCleared.map(stageLabel).join(", ")}</span>.
                  {" "}The shipper&apos;s tracker will reflect the reversion.
                </>
              ) : (
                <>The shipper&apos;s tracker will reflect the reversion.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleUndo(); }}
              disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Undo"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
